import store from '..'
import axios from "@axios";
import defaultSideBarsItems from './sidebarsDefaultConfig';
import { removeDuplicates, removeCrossDuplicates, forceCollapsibleAndLog, mergeMissingWidgets, normalizeWidgetPositions } from "@/utils/removeDuplicates";
import layout from './layout';
import tabs from './tabs';
import widgets from './widgets';
import sidebars from './sidebars';
import websockets from './websockets';
import downloads from './downloads';
import views from './views';
import units from './units';
import subsystemTree from './subsystemTree';
import videoTours from './videoTours';
import { debounce, reject } from 'lodash';

export default {
  namespaced: true,

  // State =========================================================
  state: {

    treeConfig: JSON.parse(localStorage.getItem('treeConfig')) || [],
    visited: JSON.parse(localStorage.getItem('visited')) || [],

    // Layout config
    ...layout.state,

    // Tabs
    ...tabs.state,

    // Sidebar / Bottom bar
    ...sidebars.state,

    // Widgets
    ...widgets.state,

    //downloads notifications
    ...downloads.state,

    // Views
    ...views.state,

    // Units
    ...units.state,
    ...subsystemTree.state,

    ...videoTours.state,

    alarms: []
  },
  getters: {

    userLayouts() {
      return store.state.layouts.userLayouts.map((l) => ({
        id: l.id,
        name: l.name,
        grid: l.data,
      }));
    },

    // Layout config
    ...layout.getters,

    // Tabs
    ...tabs.getters,

    ...sidebars.getters,

    // downloads notifications
    ...downloads.getters,

    // Widgets
    ...widgets.getters,

    // Views
    ...views.getters,

    // Units
    ...units.getters,
    ...subsystemTree.getters,
    ...videoTours.getters,

    getAlarms(state) {
      return state.alarms.filter((alarm) => {
        if (alarm.is_assigned_to_current_user === true) {
          return alarm.alarm_status === "waiting";
        }
        return true;
      });
    },

    // Subsystem tree config (local storage)
    getAllTrees: state => {
      return state.treeConfig;
    },
    getTreeById: state => id => {
      return state.treeConfig.find(tree => tree.tenantId === id);
    },

    // Visted items
    getVisited: state => {
      return state.visited;
    },
    getVisitedById: state => id => {
      return state.visited.find(item => item.id === id);
    },
  },

  // Mutations =========================================================
  mutations: {
    // Layout config
    ...layout.mutations,

    // Tabs
    ...tabs.mutations,

    // Sidebar / Bottom bar
    ...sidebars.mutations,

    // Widgets
    ...widgets.mutations,

    // Views
    ...views.mutations,

    // Units
    ...units.mutations,
    ...subsystemTree.mutations,

    ...videoTours.mutations,

    addNotificationAlarm(state, value) {
      state.alarms = state.alarms.filter(alarm => alarm.id != value.id)
      state.alarms.push(value)
    },
    removeNotificationAlarm(state, value) {
      state.alarms = state.alarms.filter(
        alarm =>
          (value.id == null || alarm.id != value.id) &&
          (value.alarmId == null || alarm.alarmId != value.alarmId)
      );
    },
    removeAllNotificationAlarm(state) {
      state.alarms = state.alarms.filter(alarm => alarm.is_assigned_to_current_user === true);
    },

    // Subsystem tree config (local storage)
    setTreeConfig(state, config) {
      state.treeConfig = config;
    },
    addTree(state, tree) {
      state.treeConfig.push(tree);
    },
    updateTree(state, payload) {
      const config = state.treeConfig.find(item => item.tenantId == payload.tenantId);
      if (payload.entity === 'tenant') {
        config.open = payload.open;
      } else {
        const unit = config.children.find(item => item.unitId == payload.unitId);
        if (unit) {
          unit.open = payload.open;
        } else {
          config.children.push(payload);
        }
      }
    },
    deleteTree(state, tenantId) {
      const index = state.treeConfig.findIndex(item => item.tenantId == tenantId);
      if (index >= 0) {
        state.treeConfig.splice(index, 1);
      }
    },

    // Visted items
    addVisited(state, item) {
      state.visited.push(item);
    },
    removeVisited(state, item) {
      state.visited = state.visited.filter(itm => itm.id !== item.id && itm.type === item.type);
    },
    setVisited(state, visited) {
      state.visited = visited;
    },
    clearVisited(state) {
      state.visited = [];
    },
  },

  actions: {
    // General
    async initializeStore({ commit }, data) {
      if (data) {

        // Para simular el default
        // data.sideBar = undefined;
        // data.sideBarRight = undefined;
        // data.bottomBar = [];

        store.dispatch('grid/setTab', data.tabs);
        store.dispatch('grid/setActiveTab', data.activeTab);
        store.commit('grid/updateSidebarWidth', data.sideBarWidth);
        store.commit('grid/updateSidebarRightWidth', data.sideBarRightWidth);

        const gridConfig = {
          sideBar: store.state.grid.sideBarItems,
          sideBarWidth: store.state.grid.sidebarWidth,
          bottomBar: store.state.grid.bottomBarItems,
          tabs: store.state.grid.tabs,
        };

        const isFirstConfig =
          data.sideBar === undefined &&
          data.sideBarRight === undefined;

        const isRightSidebarNeverConfigured =
          data.sideBarRight === undefined;

        if (isFirstConfig) {
          commit('setSideBarItems', defaultSideBarsItems.sideBarItems);
          commit('setSideBarRightItems', defaultSideBarsItems.sideBarRightItems);
          // bottomBar no se usa nunca, pero si vinieran defaults:
          // commit('setBottomBarItems', defaultSideBarsItems.bottomBarItems);
          return;
        }

        let sideBar = removeDuplicates(
          data.sideBar !== undefined
            ? data.sideBar
            : defaultSideBarsItems.sideBarItems
        );

        let sideBarRight = removeDuplicates(
          data.sideBarRight !== undefined
            ? data.sideBarRight
            : defaultSideBarsItems.sideBarRightItems
        );

        const cleaned = removeCrossDuplicates(sideBar, sideBarRight);

        if (data.bottomBar?.length) {
          for (const item of data.bottomBar) {
            if (!cleaned.sideBar.includes(item)) {
              cleaned.sideBar.push(item);
            }
          }
          cleaned.sideBar = removeDuplicates(cleaned.sideBar);
        }

        const finalSideBar = forceCollapsibleAndLog(cleaned.sideBar);
        const finalSideBarRight = forceCollapsibleAndLog(cleaned.sideBarRight);

        // Merge con defaults para agregar widgets nuevos (ej: user_alarms)
        let mergedSideBar;

        if (data.sideBar === undefined) {
          mergedSideBar = defaultSideBarsItems.sideBarItems;
        } else {
          const rightIds = new Set(finalSideBarRight.map(w => w.id));
          const filteredDefaults = defaultSideBarsItems.sideBarItems.filter(
            def => !rightIds.has(def.id)
          );
          mergedSideBar = mergeMissingWidgets(finalSideBar, filteredDefaults);
        }

        let mergedSideBarRight;

        if (isRightSidebarNeverConfigured) {
          const leftIdsInit = new Set(mergedSideBar.map(w => w.id));
          mergedSideBarRight = defaultSideBarsItems.sideBarRightItems.filter(
            def => !leftIdsInit.has(def.id)
          );
        } else {
          mergedSideBarRight = finalSideBarRight;
        }

        const leftIds = new Set(mergedSideBar.map(w => w.id ?? w.componentName));
        const seenRight = new Set();
        const mergedArrays = [
          ...mergedSideBarRight,
          ...defaultSideBarsItems.sideBarRightItems,
        ].filter(item => {
          const key = item.id ?? item.componentName;
          if (leftIds.has(key)) return false;
          if (seenRight.has(key)) return false;
          seenRight.add(key);
          return true;
        });

        commit('setSideBarItems', mergedSideBar);
        commit('setSideBarRightItems', mergedArrays);
        commit('setBottomBarItems', []);

        const sig = arr => (arr ?? []).map(w => w.id ?? w.componentName).join('|');
        const configWasDirty =
          sig(data.sideBar) !== sig(mergedSideBar) ||
          sig(data.sideBarRight) !== sig(mergedArrays) ||
          (data.bottomBar?.length ?? 0) > 0;

        if (configWasDirty) {
          store.dispatch('grid/updateUserConfig');
        }

        const videoTourEntry = data?.tabs
          ? Object.entries(data.tabs).find(
              ([, tab]) => tab?.type === 'video-tour' && tab.videoTour?.id
            )
          : null;

        if (videoTourEntry) {
          const [videoTourTabId, tabConfig] = videoTourEntry;
          store.dispatch('grid/resumeVideoTourFromConfig', {
            tabId: videoTourTabId,
            videoTourConfig: tabConfig.videoTour,
            activateTab: data.activeTab === videoTourTabId,
          });
        }
      }
    },

    // Layout config
    ...layout.actions,

    // Tabs
    ...tabs.actions,

    // Sidebars
    ...sidebars.actions,

    // Widgets
    ...widgets.actions,

    // Websockets
    ...websockets.actions,

    // downloads notifications
    ...downloads.actions,

    // Views
    ...views.actions,

    // Units
    ...units.actions,
    ...subsystemTree.actions,
    ...videoTours.actions,

    // User
    updateUserConfig: debounce(function ({ commit, state }) {
      store.dispatch('user/updateUserConfig', {
        grid: {
          activeTab: state.activeTab,
          tabs: state.tabs,
          sideBar: state.sideBarItems,
          sideBarWidth: state.sidebarWidth,
          sideBarRight: state.sideBarRightItems,
          sideBarRightWidth: state.sidebarRightWidth,
          bottomBar: state.bottomBarItems,
        }
      })
    }, 800),

    addNotificationAlarm({ commit, state }, params) {
      commit('addNotificationAlarm', params);
    },
    removeNotificationAlarm({ commit, state }, params) {
      commit('removeNotificationAlarm', params);
    },
    removeAllNotificationAlarm({ commit, state }) {
      commit('removeAllNotificationAlarm');
    },

    async fetchAssignedAlarmNotifications({ commit }) {
      const userId = store.state.user?.user?.id;
      if (userId == null) return;
      try {
        const response = await axios.get(`/v1/assigned_alarm_user/${userId}`);
        const list = response?.data?.data ?? response?.data ?? [];
        if (!Array.isArray(list)) return;
        const waitingOnly = list.filter((alarm) => alarm.alarm_status === "waiting");
        waitingOnly.forEach((alarm) => {
          const notification = {
            ...alarm,
            is_assigned_to_current_user: true,
          };
          commit("addNotificationAlarm", notification);
        });
      } catch (_) {}
    },

    // Subsystem tree config (local storage)
    loadTreeConfig({ commit }) {
      const config = localStorage.getItem('treeConfig');
      if (config) {
        commit('setTreeConfig', JSON.parse(config));
      }
    },
    saveTreeConfig({ state }) {
      localStorage.setItem('treeConfig', JSON.stringify(state.treeConfig));
    },
    addTree({ commit, dispatch }, tree) {
      commit('addTree', tree);
      dispatch('saveTreeConfig');
    },
    updateTree({ commit, dispatch }, payload) {
      commit('updateTree', payload);
      dispatch('saveTreeConfig');
    },
    deleteTree({ commit, dispatch }, index) {
      commit('deleteTree', index);
      dispatch('saveTreeConfig');
    },

    // Visited items
    loadVisited({ commit }) {
      const visited = localStorage.getItem('visited');
      if (visited) {
        commit('setVisited', JSON.parse(visited));
      }
    },
    addVisited({ commit, dispatch, state }, item) {
      if (!state.visited.find(v => v.id === item.id)) {
        commit('addVisited', item);
        dispatch('saveVisited');
      }
    },
    removeVisited({ commit, dispatch }, item) {
      commit('removeVisited', item);
      dispatch('saveVisited');
    },
    clearVisited({ commit }) {
      commit('clearVisited');
      localStorage.removeItem('visited');
    },
    saveVisited({ state }) {
      localStorage.setItem('visited', JSON.stringify(state.visited));
    },

    clear({ commit }) {
      commit('setTenants', [])
      commit('setUnits', [])
      commit('setUnitsLoadStatus', false)
      commit('removeAllDownloadNotification', false)
      commit('removeAllNotificationAlarm')
    },
  },
}
